'use client';
import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { Loading } from '@/app/components/icons/Loading';
import { useCommunityInteraction } from '@/app/components/shared/Community';
import { openAlertDialog } from '@/app/components/shared/imperative-dialog';
import { DialogClose, DialogContent } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { MODEL_VERSION, SITE, TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import {
  ErrorMsg,
  useCheckEnableCreateElement,
  useCopy,
  useElementErrorMsg,
  useFetch,
  useGlobalModal,
  useLoginRequired,
  useRecreate,
} from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ReferenceModalState } from '@/app/stores/slices/reference-modal';
import type { TaskInputPrompts } from '@/app/types';
import { CommunityEnum, type CreationWithTask, EllipsisActionTypeEnum, type Option } from '@/app/types';
import { cn, convertViduV1ElementToElementType, reportEvent } from '@/app/utils';
import { generateCommunityShareUrlWithParams } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import {
  materialsCancelShareElement,
  materialsCopyFromShare,
  materialsCreateElementCheck,
  materialsGetShareCategories,
  materialsSubmitShareElement,
} from './apis';
import { CreationPostModal } from './components/CreationPostModal';
import { FooterBtnGroup } from './components/FooterBtnGroup';
import { LeftPanel } from './components/LeftPanel';
import { RightPanel } from './components/RightPanel';
import { TitleSection } from './components/TitleSection';
import { UpdateItemDialog } from './components/UpdateItemDialog';
import { useUpdateItemModal } from './hooks/use-update-item-modal';
import { postReferencesReducer } from './reducer';
import type { ElementDetailAction, SubmitElementDetail } from './types';
import { convertElementDetail2SubmitElementDetail, toTitleSectionMode } from './utils';

export const ReferenceDetail = ({
  onClose,
}: {
  onClose: (reason?: ReferenceModalState['referenceModalUpdateReason']) => void;
}) => {
  const t = useTranslations();
  const [state, dispatch] = useReducer(postReferencesReducer, undefined);

  const { showErrorMsg } = useElementErrorMsg({
    [ErrorMsg.AuditSubmitIllegal]: t('elements.submit.dialog.form.submitFail'),
  });

  const [openCreationPost, setOpenCreationPost] = useState<boolean>(false);
  const [categoryOptions, setCategoryOptions] = useState<Option<string>[]>([]);
  const initialNameRef = useRef('');
  const replaceVideoIndexRef = useRef<number>(-1);
  const { copy: copyText } = useCopy();
  const { open: openActionDialog } = useGlobalModal('actionDialog');
  const { open: openSimpleCreationDetailModal } = useGlobalModal('simpleCreationDetailModal');
  const pathname = usePathname();
  const {
    userInfo,
    isTeamSpace,
    referenceModalMode,
    referenceModalFrom,
    reference,
    referenceModalType,
    updateReferenceModalUpdateReason,
    updateReferenceModalMode,
    referenceModalOpen,
    referenceModalFromPage,
    isUserInfoInitialized,
  } = useStore((state) => ({
    reference: state.reference,
    referenceModalFrom: state.referenceModalFrom,
    referenceModalFromPage: state.referenceModalFromPage,
    referenceModalMode: state.referenceModalMode,
    referenceModalType: state.referenceModalType,
    referenceModalOpen: state.referenceModalOpen,
    updateReferenceModalUpdateReason: state.updateReferenceModalUpdateReason,
    updateReferenceModalMode: state.updateReferenceModalMode,
    userInfo: state.userInfo,
    isTeamSpace: state.isTeamSpace,
    isUserInfoInitialized: state.isUserInfoInitialized,
  }));

  const { checkLoginAndExecute } = useLoginRequired();

  useEffect(() => {
    if (referenceModalOpen) {
      initialNameRef.current = reference?.element?.name || '';
    }
  }, [referenceModalOpen]);

  const handleOpenReportDialog = useCallback(() => {
    if (referenceModalType === 'share' && state?.shareElementId) {
      openActionDialog({
        action: EllipsisActionTypeEnum.InspirationReport,
        id: state.shareElementId,
        type: 'share_element',
      });
    }
  }, [referenceModalType, state?.shareElementId]);

  const onReportEvent = (name: string) => {
    reportEvent('sharedreference_details_click', {
      element_name: name,
      from_page: referenceModalFromPage || '',
      reference_id: reference?.element?.id || '',
    });
  };

  const changeItem = useMemoizedFn(async (params: ElementDetailAction) => {
    const { type, value } = params;
    switch (type) {
      case 'updateName': {
        if (value !== initialNameRef.current) {
          const res = await checkElementNameAsync(value);
          if (res.data?.ok) {
            dispatch({ type: 'updateName', value: value });
            return true;
          }
          return false;
        }
        dispatch({ type: 'updateName', value: value });
        return true;
      }
      case 'updateStyle':
        dispatch({ type: 'updateStyle', value: value });
        return true;
      case 'updateDescription':
        dispatch({ type: 'updateDescription', value: value });
        return true;
      case 'updateReactInfo': {
        dispatch({ type: 'updateReactInfo', value: value });
        return true;
      }
      case 'updateCategories': {
        dispatch({ type: 'updateCategories', value: value });
        return true;
      }
      case 'updateSeries': {
        dispatch({ type: 'updateSeries', value: value });
        return true;
      }
      case 'updateIntroduce': {
        dispatch({ type: 'updateIntroduce', value: value });
        return true;
      }
      case 'updateCreations': {
        dispatch({ type: 'updateCreations', value: value });
        return true;
      }
      case 'updateAllowCopy': {
        dispatch({ type: 'updateAllowCopy', value: value });
        return true;
      }
      default:
        return true;
    }
  });

  const {
    config: updateItemDialogConfig,
    isOpen: isUpdateItemDialogOpen,
    setIsOpen: setUpdateItemDialogOpen,
  } = useUpdateItemModal({
    onSave: changeItem,
  });

  useEffect(() => {
    dispatch({
      type: 'init',
      value: reference || undefined,
    });
  }, [reference]);

  const { run: cancelSubmit, loading: cancelSubmitLoading } = useFetch(
    () =>
      materialsCancelShareElement({
        body: {
          share_id: state?.shareElementId || '',
        },
        path: {
          share_id: state?.shareElementId || '',
        },
      }),
    {
      manual: true,
      onSuccess: (data) => {
        if (data.error) {
          showErrorMsg(data.error);
        } else {
          onClose({
            reference: convertViduV1ElementToElementType({
              ...state,
              creator_id: state?.creator?.id,
              share_meta: undefined,
              type: 'user',
            }),
            type: 'update',
          });
          closeConfirmDialog();
        }
      },
    },
  );

  const { run: copy, loading: copyLoading } = useFetch(
    () => {
      if (referenceModalType !== 'share' || !state?.shareElementId) {
        return Promise.reject(new Error('Invalid share element'));
      }
      return materialsCopyFromShare({
        body: {
          share_id: state.shareElementId,
        },
        path: {
          share_id: state.shareElementId,
        },
      });
    },
    {
      manual: true,
      onSuccess: (data) => {
        if (data.error) {
          showErrorMsg(data.error);
        } else {
          toast({
            message: t('elements.actionSuccess'),
            type: 'success',
          });
          updateReferenceModalUpdateReason({
            type: 'add',
          });
          // 作者复制自己的主体，不计数，复制他人的才计数
          if (!isAuthor) {
            const count = (state?.shareCopyCount || 0) + 1;
            dispatch({
              type: 'updateShareCopyCount',
              value: count,
            });
            updateReferenceModalUpdateReason({
              count: count,
              id: state?.shareElementId || '',
              type: 'copy',
            });
          }
        }
      },
    },
  );

  useFetch(() => materialsGetShareCategories(), {
    ready: isUserInfoInitialized && !isTeamSpace,
    onSuccess: (data) => {
      if (data.error) {
        showErrorMsg(data.error);
      } else {
        setCategoryOptions(
          data.data.categories?.map((it) => ({ label: it.display_name || '', value: it.code || '' })) || [],
        );
      }
    },
  });

  const { loading: submitLoading, run: submit } = useFetch(
    (params: SubmitElementDetail) =>
      materialsSubmitShareElement({
        body: params,
      }),
    {
      manual: true,
      onSuccess: (data) => {
        if (data.error) {
          showErrorMsg(data.error, 'error', t('elements.submit.dialog.form.requestFail'));
        } else {
          toast({
            message: t('elements.submit.dialog.form.submitSuccess'),
            type: 'success',
          });
          onClose({
            reference: convertViduV1ElementToElementType({
              ...data.data.element,
              type: 'user',
            }),
            type: 'update',
          });
        }
      },
    },
  );

  const { vote: updateVote, unVote: updateUnVote } = useCommunityInteraction({
    onInteractionStatusChange: (params) => {
      const reactInfo = {
        ...state?.reactInfo,
        vote_count: (state?.reactInfo?.vote_count ?? 0) + (params.voted ? 1 : -1),
        voted: params.voted,
      };
      params.voted && onReportEvent('like');
      dispatch({ type: 'updateReactInfo', value: reactInfo });
      updateReferenceModalUpdateReason({
        id: params.id,
        type: 'like',
        voted: params.voted || false,
      });
    },
  });

  const { collect: updateCollect, unCollect: updateUnCollect } = useCommunityInteraction({
    onInteractionStatusChange: (params) => {
      const reactInfo = {
        ...state?.reactInfo,
        collect_count: (state?.reactInfo?.collect_count || 0) + (params.collected ? 1 : -1),
        collected: params.collected,
      };
      params.collected && onReportEvent('collect');
      dispatch({ type: 'updateReactInfo', value: reactInfo });
      updateReferenceModalUpdateReason({
        collect_count: reactInfo.collect_count,
        collected: reactInfo.collected || false,
        id: params.id,
        type: 'collect',
      });
    },
  });

  const handleSubmit = () => {
    if (SITE === 'china') {
      return;
    }

    if (state?.voice?.is_my) {
      return;
    }

    reportEvent('publish_reference_confirm', {
      from_page: referenceModalFromPage || '',
    });
    submit(convertElementDetail2SubmitElementDetail(state));
  };

  const disableSubmit = useMemo(() => {
    if (referenceModalMode !== 'post') {
      return false;
    }
    const isTextModality = state?.modality === 'text';
    return (
      SITE === 'china' ||
      submitLoading ||
      state?.voice?.is_my ||
      !state?.category?.length ||
      !state?.creations?.length ||
      (!isTextModality && (!state?.style?.trim().length || !state?.description?.trim().length))
    );
  }, [
    state?.category,
    submitLoading,
    state?.creations,
    state?.style,
    state?.description,
    state?.modality,
    state?.voice?.is_my,
    referenceModalMode,
  ]);

  const { open: openConfirmDialog, close: closeConfirmDialog } = useGlobalModal('confirmDialog');
  const { checkCreateElementEnable } = useCheckEnableCreateElement({
    onSuccess: () => {
      copy();
    },
  });

  const handleCancel = () => {
    if (referenceModalType !== 'share' || !state?.shareElementId) {
      return;
    }
    onReportEvent('cancel_publish');

    openConfirmDialog({
      content: isSubmitSuccess
        ? t('elements.submit.dialog.form.approvedAndCancel.title')
        : t('elements.submit.dialog.form.pendingAndCancel.title'),
      onPrimaryAction: cancelSubmit,
      primaryButtonText: t('elements.deleteShareReference.rightBtn'),
      secondaryButtonText: t('elements.deleteShareReference.leftBtn'),
    });
  };

  const { runAsync: checkElementNameAsync } = useFetch(
    (name?: string) => {
      return materialsCreateElementCheck({
        query: {
          name: name,
        },
      });
    },
    {
      manual: true,
      onSuccess: (data) => {
        if (data.error) {
          showErrorMsg(data.error);
        }
      },
    },
  );

  const handleCopy = () => {
    if (referenceModalType !== 'share' || !state?.shareElementId) {
      return;
    }
    onReportEvent('copy');
    checkCreateElementEnable({
      query: {},
    });
  };

  const isAuthor = useMemo(() => {
    if (referenceModalType === 'share') {
      return userInfo?.id === state?.creator?.id;
    }
    return userInfo?.id === reference?.element?.creator_id;
  }, [userInfo?.id, state?.creator?.id, reference?.element?.creator_id, referenceModalType]);

  const isSubmitSuccess = useMemo(() => {
    return referenceModalType === 'share' && state?.shareStatus === 'approved';
  }, [state?.shareStatus, referenceModalType]);
  const isSubmitForbidden = SITE === 'china' || state?.voice?.is_my === true;

  const { handleRecreate } = useRecreate();

  const goToUseReference = () => {
    onReportEvent('use');
    const prompts = [
      {
        content: `[@${state?.name}]`,
        type: 'text' as TaskInputPrompts['type'],
      },
      {
        content: state?.components?.map((it) => it.content),
        material: {
          id: state?.id || '',
          version: state?.version || '',
        },
        name: state?.name,
        type: 'material' as TaskInputPrompts['type'],
      },
    ];
    const modelVersion = state?.modality === 'video' ? MODEL_VERSION.Q2_PRO : MODEL_VERSION.Q2;
    handleRecreate({
      creationId: '',
      input: {
        enhance: true,
        prompts: prompts as TaskInputPrompts[],
      },
      settings: {
        model_version: modelVersion,
        duration: 5,
        resolution: '1080p',
      },
      taskId: '',
      taskType: 'character2video',
    });
    onClose({
      type: 'use',
    });
  };

  const onCancelSelect = () => {
    onClose({
      reference: {
        id: state?.id || '',
        modality: 'text',
      },
      type: 'cancelSelect',
    });
  };

  const handleEdit = () => {
    updateReferenceModalMode('edit');
  };

  const handleSubmitToCommunity = () => {
    updateReferenceModalMode('post');
  };

  const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const needCheckLoginStatus = !target.closest('.skip-login-check');

    if (needCheckLoginStatus) {
      checkLoginAndExecute({
        onNeedLogin: () => {
          e.stopPropagation();
        },
        targetUrl: generateCommunityShareUrlWithParams({
          id: state?.id || '',
          pathname: '/home/reference',
        }),
      });
    }
  };

  const handleShareClick = () => {
    const url = generateCommunityShareUrlWithParams({
      id: state?.id || '',
      pathname: '/home/reference',
    });
    copyText(url, {
      hideMessage: true,
    });
    openAlertDialog({
      buttonText: t('elements.deleteElement.rightBtn'),
      closable: false,
      description: t('create.share.description'),
    });
    if (referenceModalType === 'share' && state?.shareElementId) {
      reportEvent(TRACE_EVENT_NAME[CommunityEnum.PostReferences], {
        [TRACE_EVENT_ID[CommunityEnum.PostReferences]]: state.shareElementId,
        interaction_position: pathname,
        interaction_type: 'share',
        content_type: 'video',
      });
    }
  };

  return (
    <>
      <DialogContent
        className={cn(
          'h-full max-h-[652px] min-w-[900px] border-system-white12 bg-none bg-transparent p-0 sm:h-full sm:min-h-full sm:w-full sm:min-w-full sm:rounded-none sm:bg-[#17191B] sm:p-0 md:p-0',
        )}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        disableOverlayClose={true}
        onClickCapture={referenceModalFrom === 'share' ? handleDialogClick : undefined}
        closable={false}
      >
        <div className="relative flex size-full overflow-hidden bg-[#17191B] sm:block sm:overflow-y-scroll sm:pt-16">
          {/* 移动端头部 */}
          <div className="fixed top-0 right-0 left-0 z-10 hidden h-16 items-center justify-center bg-[#17191B] sm:flex sm:px-10">
            <TitleSection
              mode={toTitleSectionMode(referenceModalMode)}
              isAuthor={isAuthor}
              onReport={handleOpenReportDialog}
              shareStatus={state?.shareStatus}
              handleShareClick={handleShareClick}
            />
            <BaseLineLeft className="absolute top-5 left-5 size-5" onClick={() => onClose()} />
          </div>

          <DialogClose className="skip-login-check absolute top-3 right-6 size-5 sm:hidden" />

          {/* web左侧内容 */}
          <LeftPanel
            mode={toTitleSectionMode(referenceModalMode)}
            isAuthor={isAuthor}
            isSubmitSuccess={isSubmitSuccess}
            onReport={handleOpenReportDialog}
            state={state}
            referenceModalType={referenceModalType}
            handleShareClick={handleShareClick}
          />

          {/* web右侧内容 */}
          <RightPanel
            isAuthor={isAuthor}
            isSubmitSuccess={isSubmitSuccess}
            state={state}
            changeItem={changeItem}
            categoryOptions={categoryOptions}
            handleCancel={handleCancel}
            updateVote={updateVote}
            updateUnVote={updateUnVote}
            handleSubmit={handleSubmit}
            handleCopy={handleCopy}
            disableSubmit={disableSubmit}
            onCancelSelect={onCancelSelect}
            handleCollect={updateCollect}
            handleUnCollect={updateUnCollect}
            openCreationPost={(open, index) => {
              setOpenCreationPost(open);
              replaceVideoIndexRef.current = index ?? -1;
            }}
            openCreation={(index: number) => {
              const creation = state?.creations?.[index];
              if (!creation) return;
              openSimpleCreationDetailModal({ creation });
            }}
            handleUse={goToUseReference}
            onReportEvent={onReportEvent}
            handleClose={() => {
              onClose();
            }}
            handleEdit={handleEdit}
            handleSubmitToCommunity={handleSubmitToCommunity}
            elementType={reference?.element?.type}
            isCommercial={reference?.element?.pay_info?.pay_required ?? false}
          />

          {(submitLoading || cancelSubmitLoading || copyLoading) && (
            <span className="absolute inset-0 flex items-center justify-center bg-system-black36">
              <Loading className="animate-spin text-2xl" />
            </span>
          )}

          {/* 移动端底部按钮 */}
          <div className="hidden bg-[#17191B] p-4 sm:fixed sm:right-0 sm:bottom-0 sm:left-0 sm:flex">
            <FooterBtnGroup
              className="w-full"
              isTeamSpace={isTeamSpace}
              mode={toTitleSectionMode(referenceModalMode)}
              isAuthor={isAuthor}
              isSubmitSuccess={isSubmitSuccess}
              shareElementId={state?.shareElementId}
              reactInfo={state?.reactInfo}
              from={referenceModalFrom}
              allowCopy={state?.allowCopy}
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              onCopy={handleCopy}
              onUse={goToUseReference}
              disableSubmit={disableSubmit}
              isSubmitForbidden={isSubmitForbidden}
              referenceModalType={referenceModalType}
              elementType={reference?.element?.type}
              onEdit={handleEdit}
              onSubmitToCommunity={handleSubmitToCommunity}
              updateVote={updateVote}
              updateUnVote={updateUnVote}
              onCancelSelect={onCancelSelect}
              handleCollect={updateCollect}
              handleUnCollect={updateUnCollect}
              canCollect={state?.shareStatus === 'approved'}
              handleClose={() => {
                onClose();
              }}
              isCommercial={reference?.element?.pay_info?.pay_required ?? false}
            />
          </div>
        </div>
      </DialogContent>

      {openCreationPost && (
        <CreationPostModal
          open={openCreationPost}
          disabledIdList={state?.creations?.map((it) => it.id) || []}
          elementId={reference?.element?.id}
          onClose={(creation?: CreationWithTask) => {
            setOpenCreationPost(false);
            if (creation) {
              const list = [...(state?.creations || [])];
              if (list[replaceVideoIndexRef.current]) {
                list[replaceVideoIndexRef.current] = creation;
              } else {
                list.push(creation);
              }
              dispatch({ type: 'updateCreations', value: list });
            }
          }}
        />
      )}

      {updateItemDialogConfig && (
        <UpdateItemDialog
          open={isUpdateItemDialogOpen}
          setOpen={setUpdateItemDialogOpen}
          label={updateItemDialogConfig.label || ''}
          placeholder={updateItemDialogConfig.placeholder || ''}
          initValue={updateItemDialogConfig.value || ''}
          type={updateItemDialogConfig.inputType || 'input'}
          maxLength={updateItemDialogConfig.maxLength}
          rows={updateItemDialogConfig.rows}
          required={updateItemDialogConfig.required}
          onSave={updateItemDialogConfig.handleSave}
        />
      )}
    </>
  );
};
